import type { ColumnDef } from "@tanstack/react-table";
import Flexbox from "../../common/Flexbox";
import TableGraph from "../../graphs/TableGraph";
import { cn } from "$/lib/utils/functions/misc.functions";
import { Tabs } from "../../molecules";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import { DEFAULT_TABLE_PERIOD_TABS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import type { ThemeChoices } from "$/lib/types/General.types";

type Props<T extends object> = {
  data: T[];
  columns: ColumnDef<T, number | string>[];
  tableClassName?: string;
  tableBounds: {
    max: {
      value: number;
    };
    min: {
      value: number;
    };
  };
  hasLegend?: boolean;
  reverse?: boolean;
  selectedPeriod: FutureProductPeriod | "DAY";
  setSelectedPeriod: (id: FutureProductPeriod | "DAY") => void;
  theme?: ThemeChoices;
};

const TableGraphWidget = <T extends object>({
  data,
  columns,
  tableClassName,
  tableBounds,
  hasLegend = true,
  reverse = false,
  selectedPeriod,
  setSelectedPeriod,
  theme = "blue",
}: Props<T>) => {
  const min = tableBounds.min.value;
  const max = tableBounds.max.value;

  // const neutralPoint = ((0 - min) / (max - min)) * 100;

  return (
    <Flexbox
      fullWidth
      className="max-h-96 max-w-[310px] gap-4 overflow-auto"
      align="stretch"
    >
      {hasLegend && (
        <Flexbox className="w-full gap-2 px-2" align="stretch">
          <p className="whitespace-nowrap text-base font-semibold text-secondary">
            Légende Tableau
          </p>
          <Flexbox row className="w-full shrink-0 gap-2" align="center">
            <p className="text-sm font-medium text-primary">{min}</p>
            <div
              className={cn("relative h-6 w-full grow", {
                "bg-gradient-to-r from-forest to-danger": !reverse,
                "bg-gradient-to-r from-danger to-forest": reverse,
              })}
            >
              {/* <div className="absolute -bottom-0.5 left-6 h-2 w-0.5 bg-black" /> */}
            </div>
            <p className="text-sm font-medium text-primary">{max}</p>
          </Flexbox>
        </Flexbox>
      )}

      <Tabs
        className="mx-auto w-full"
        navClassName="w-full flex flex-row items-center justify-between"
        tabClassName="!text-xs"
        type="bordered"
        tabs={DEFAULT_TABLE_PERIOD_TABS}
        activeTab={selectedPeriod}
        handleTabChange={(id) =>
          setSelectedPeriod(id as FutureProductPeriod | "DAY")
        }
        theme={theme}
      />

      <TableGraph<T>
        columns={columns}
        data={data}
        className={cn(
          "w-full rounded-lg border border-gray-200 shadow-sm",
          tableClassName,
        )}
      />
    </Flexbox>
  );
};

export default TableGraphWidget;
